import React from "react";
import "../common/NoticesAndLitigationDetail.css";
import { useState, useEffect,useRef } from "react";
import downloadButton from "./../../../../assets/img/icon/downloadButton.svg";
import editIcon from "./../../../../assets/img/icon/editIcon.svg";
import { hasUserAccess, getUserDetails } from "../../../../backend/auth";
import { USER_PRIVILEGES } from "../../UserManagement/Roles/RoleConfiguration";
import NoticesEditDetails from "./NoticesEdit";
import PageLoader from "../../../shared/PageLoader";
import {
  useGetNotice,
  useUpdateNotice,
  useChangeNoticeStatus,
  useGetNoticeHistory,
} from "../../../../backend/notice";
import { toast } from "../../../../utils/toast";
import { useQueryClient } from "@tanstack/react-query";
import { downloadS3FileSecurely, formatDateDisplay } from "../../../../utils/common";
import {
  getAllowedStatusTransitions,
  getStatusLabel,
  getStatusColorClass,
  formatDateTime,
  formatIssuedDateTime
} from "../../../../backend/common/noticeManagementUtils";

interface LicenseDetailProps {
  isOpen: boolean;
  noticeDetail?: any;
  closePanel: () => void;
  refetch?: () => void;
}
interface HistoryChange {
  from: any;
  to: any;
  reason?: string;
}

interface HistoryItem {
  id: number | string;
  action: string;
  entry_type?: string;
  changed_by: string;
  changed_at: string;
  changes?: Record<string, HistoryChange>;
  document_details?: {
    filename: string;
    file_size: string;
    content_type: string;
  };
}

interface DisplayHistoryItem {
  id: number | string;
  title: string;
  date: string;
  rawDate: string;
  description: string;
  name: string;
}

export function canEditLicenseApplication(application: any) {
  const user = getUserDetails();

  const hasEditPrivilege = hasUserAccess(USER_PRIVILEGES.NOTICE_MANAGER) ||
                           hasUserAccess(USER_PRIVILEGES.NOTICE_OWNER) ||
                           hasUserAccess(USER_PRIVILEGES.EDIT_NOTICES);

  if (!hasEditPrivilege) {
    return false;
  }

  // Notice Owner: check record-level assignment
  if ((hasUserAccess(USER_PRIVILEGES.NOTICE_OWNER) || hasUserAccess(USER_PRIVILEGES.EDIT_NOTICES)) && !hasUserAccess(USER_PRIVILEGES.NOTICE_MANAGER)) {
    return (
      application.editor_ids && application.editor_ids.includes(user.user_id)
    );
  }

  // Notice Manager or has full edit access: can edit any
  return hasUserAccess(USER_PRIVILEGES.NOTICE_MANAGER) || hasUserAccess(USER_PRIVILEGES.EDIT_NOTICES);
}

export function canEditCompanyLicenseCostDetails(noticeDetail: any) {
  const user = getUserDetails();

  // Notice Manager or has edit action: can edit any
  if (hasUserAccess(USER_PRIVILEGES.NOTICE_MANAGER) || hasUserAccess(USER_PRIVILEGES.EDIT_NOTICES)) {
    return true;
  }

  // Notice Owner: can edit cost details if assigned to this noticeDetail
  if (hasUserAccess(USER_PRIVILEGES.NOTICE_OWNER)) {
    const assignedToId = noticeDetail.assigned_to || (noticeDetail.assigned_to && noticeDetail.assigned_to.id);
    return assignedToId === user.user_id;
  }

  // Notice Watcher: cannot edit
  return false;
}

export function canViewLicenseApplication(application: any) {
  const user = getUserDetails();

  // Notice Watcher: check record-level assignment
  if (hasUserAccess(USER_PRIVILEGES.NOTICE_WATCHER) && !hasUserAccess(USER_PRIVILEGES.NOTICE_OWNER) && !hasUserAccess(USER_PRIVILEGES.NOTICE_MANAGER)) {
    return (
      application.watcher_ids && application.watcher_ids.includes(user.user_id)
    );
  }

  // Manager and Owner: can view all
  return hasUserAccess(USER_PRIVILEGES.NOTICE_MANAGER) || hasUserAccess(USER_PRIVILEGES.NOTICE_OWNER) || hasUserAccess(USER_PRIVILEGES.NOTICE_WATCHER);
}

const NoticeDetail: React.FC<LicenseDetailProps> = ({
  isOpen,
  closePanel,
  noticeDetail: licenseProp,
  refetch,
}) => {
  // Fetch fresh notice data
  const { notice: fetchedNotice, isFetching: isFetchingNotice, refetch: refetchNotice } = useGetNotice(
    licenseProp && licenseProp.id ? licenseProp.id : '',
    !!(licenseProp && licenseProp.id && isOpen)
  );

  // Use fetched notice data if available, otherwise fall back to prop
  const noticeDetail = (fetchedNotice && fetchedNotice.id) ? fetchedNotice : licenseProp;

  const user = getUserDetails();
  const queryClient = useQueryClient();

  // Update mutation hook
  const { updateNotice, updating } = useUpdateNotice(
    () => {
      toast.success("Notice updated successfully");
      closeEditPopup();
      queryClient.invalidateQueries(['notices']);
      queryClient.invalidateQueries(['notice', licenseProp && licenseProp.id]);
      if (refetch) refetch();
      if (refetchNotice) refetchNotice();
      closePanel();
    },
    (error: any) => {
      const errorData = error && error.response && error.response.data;
      const errorMessages = errorData && (errorData.error_messages || errorData.errors || errorData.details);

      if (errorMessages && Array.isArray(errorMessages) && errorMessages.length > 0) {
        errorMessages.forEach((errMsg: string) => {
          toast.error(errMsg);
        });
      } else if (errorData && errorData.message) {
        toast.error(errorData.message);
      } else {
        toast.error("Failed to update notice");
      }
    }
  );

  // Status change mutation hook
  const { changeStatus, updating: updatingStatus } = useChangeNoticeStatus(
    () => {
      toast.success("Notice status updated successfully");
      queryClient.invalidateQueries(['notices']);
      queryClient.invalidateQueries(['notice', licenseProp && licenseProp.id]);
      if (refetch) refetch();
      if (refetchNotice) refetchNotice();
      setShowStatusPopup(false);
      setStatusNote("");
      setPendingStatusChange("");
      closePanel();
    },
    (error: any) => {
      const errorData = error && error.response && error.response.data;
      const errorMessages = errorData && (errorData.error_messages || errorData.errors || errorData.details);

      if (errorMessages && Array.isArray(errorMessages) && errorMessages.length > 0) {
        errorMessages.forEach((errMsg: string) => {
          toast.error(errMsg);
        });
      } else if (errorData && errorData.message) {
        toast.error(errorData.message);
      } else {
        toast.error("Failed to update notice status");
      }
    }
  );

  // State management removed for notices - not needed

  const [activeTab, setActiveTab] = useState<
    "details" | "history" | "applications" | "documents"
  >("details");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [showStatusPopup, setShowStatusPopup] = useState(false);
  const [statusNote, setStatusNote] = useState("");
  const [pendingStatusChange, setPendingStatusChange] = useState<string>("");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const statusNoteRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (noticeDetail && noticeDetail.status) {
      setSelectedStatus(noticeDetail.status);
    } else if (noticeDetail) {
      setSelectedStatus("open");
    }
  }, [noticeDetail]);

  // Reset to details tab when panel opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab("details");
    }
  }, [isOpen]);

  // Scroll to top when tab changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeTab]);

  // Scroll to status note textarea when it appears
  useEffect(() => {
    if (showStatusPopup && statusNoteRef.current) {
      setTimeout(() => {
        if (statusNoteRef.current) {
          statusNoteRef.current.focus();
          statusNoteRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }
      }, 150);
    }
  }, [showStatusPopup]);


  const handleStatusChange = (newStatus: string) => {
    if (newStatus === selectedStatus) return;
    setPendingStatusChange(newStatus);
    setStatusNote("");
    setShowStatusPopup(true);
  };

  const handleSaveStatusChange = async () => {
    if (!statusNote.trim()) {
      toast.error("Please add a note for the status change");
      return;
    }

    if (!noticeDetail || !noticeDetail.id) return;

    const payload = {
      id: noticeDetail.id,
      status: pendingStatusChange === "resolved" ? "resolved" : pendingStatusChange,
      note: statusNote,
    };

    try {
      await changeStatus(payload as any);
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const openEditPopup = () => setIsEditOpen(true);
  const closeEditPopup = () => setIsEditOpen(false);

  const canShowEditButton =
    (hasUserAccess(USER_PRIVILEGES.NOTICE_MANAGER) ||
     hasUserAccess(USER_PRIVILEGES.NOTICE_OWNER) ||
     hasUserAccess(USER_PRIVILEGES.EDIT_NOTICES)) &&
    noticeDetail &&
    noticeDetail.status !== "surrendered" &&
    noticeDetail.status !== "expired" &&
    noticeDetail.status !== "resolved";
  
  const handleClosePanel = () => {
    setActiveTab("details");
    setShowStatusPopup(false);
    setStatusNote("");
    setPendingStatusChange("");
    closePanel();
  };

  const noticeDocuments = (noticeDetail && noticeDetail.documents) || [];

  const isLoading = updating || updatingStatus || isFetchingNotice;

  const handleSave = (data: any, section?: string) => {
    if (!noticeDetail || !noticeDetail.id) return;

    const payload: any = {
      id: noticeDetail.id,
    };

    // Always include notice object at top level
    payload.notice = {
      ...(data.notice || {}),
      updated_by_id: user.user_id,
    };

    // Include document_details if present
    if (data.document_details) {
      payload.document_details = data.document_details;
    }

    updateNotice(payload as any);
  };

  const noticeDetails = noticeDetail
    ? [
        { label: "Notice Name", value: noticeDetail.name ||"-" },
        { label: "Description", value: noticeDetail.description || "-" },
        { label: "Issued Date", value: formatDateDisplay(noticeDetail.issued_date || noticeDetail.issued_date) },
        { label: "Resolution Date", value: formatDateDisplay(noticeDetail.resolution_date || noticeDetail.expiry_date) },
        { label: "Issued by", value: noticeDetail.issued_by || noticeDetail.authority || "-" },
        {
          label: "Company",
          value: (noticeDetail.company && noticeDetail.company.name) || "-",
        },
        {
          label: "Associate Company",
          value:
            (noticeDetail.associate_company && noticeDetail.associate_company.name) ||
            "-",
        },
        {
          label: "Location",
          value: noticeDetail.location  ? `${noticeDetail.location.name} - ${noticeDetail.location.code}` : "-",
        },
        {
          label: "Category",
          value: (noticeDetail.category && noticeDetail.category.name) || (noticeDetail.license_type && noticeDetail.license_type.name) || "-",
        },
        {
          label: "Issued Under Act",
          value: noticeDetail.act  ? `${noticeDetail.act.establishment_type} - ${noticeDetail.act.name}` : "-",
        },
        {
          label: "Assigned To",
          value:
            (noticeDetail.owner && noticeDetail.owner.name) ||
            "-",
        },
        {
          label: "Watchers",
          value:
            (noticeDetail.watchers && noticeDetail.watchers.map((watcher: any) => watcher.name).join(", ")) ||
            "-",
        },
      ]
    : [];


  const {
      historyItems,
      isFetching: isLoadingHistory,
      refetch: refetchHistory,
    } = useGetNoticeHistory(
      noticeDetail && noticeDetail.id ? noticeDetail.id : undefined,
      "watcher_ids,created_by,updated_by,editor_ids,assigned_to,escalation_user_id,status,description,name",
      !!(noticeDetail && noticeDetail.id)
    );
    
  // Get the latest change with field updates from history API
  const formatFieldName = (field: string): string => {
    // Remove _id suffix for cleaner field names
    const cleanField = field.replace(/_id$/, '');
    return cleanField
      .replace(/_/g, ' ')
      .split(' ')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  const formatFieldValue = (value: any, field?: string): string => {
  if (value === null || value === undefined) return "(empty)";
  if (typeof value === "boolean") return value ? "Yes" : "No";

  if (typeof value === "string") {
    if (value.trim() === "") return "(empty)";

    if (field === "status") {
      // Format status values: remove underscores and capitalize
      return value === 'in_progress' ? 'In Progress' :
             value === 'on_hold' ? 'On Hold' :
             value === 'resolved' ? 'Resolved' :
             value === 'open' ? 'Open' :
             value.replace(/_/g, ' ').split(' ').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    }

    if (field === "category_id" && noticeDetail && noticeDetail.category) {
      if (value === noticeDetail.category.id) {
        return noticeDetail.category.name;
      }
      return value;
    }

    if (field && (field.includes('_date') || field === 'date') && !isNaN(Date.parse(value))) {
      return formatIssuedDateTime(value);
    }

    return value;
  }

  if (typeof value === "object" && value.id && value.name) {
    return value.name;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return "None";
    return value
      .map((item: any) =>
        typeof item === "object" && item.name ? item.name : String(item)
      )
      .join(", ");
  }

  return String(value);
};

  const displayHistoryItems: DisplayHistoryItem[] = [];

  if (noticeDetail) {
    // 1. Notice Issued (if issued_date exists)
    if (noticeDetail.issued_date) {
      const issuedBy = (noticeDetail.creator && noticeDetail.creator.name) || "-";

      displayHistoryItems.push({
        id: 1,
        title: "Notice Issued",
        date: formatIssuedDateTime(noticeDetail.issued_date),
        rawDate: noticeDetail.issued_date,
        name: `Action taken by <strong style="color:#2965AD" class="text-capitalize">${issuedBy}</strong>`,
        description: "",
      });
    }

    // 2. Notice Edited - Create ONE entry per change object with all fields
    if (historyItems && historyItems.length > 0) {
      historyItems.forEach((historyItem: HistoryItem, historyIndex: number) => {
        if (historyItem.changes && Object.keys(historyItem.changes).length > 0) {
          // Filter out metadata field
          const relevantFields = Object.keys(historyItem.changes).filter((field: string) => field !== 'metadata');

          // Skip this history item if only metadata exists
          if (relevantFields.length === 0) {
            return;
          }

          // Check if this is a status change
          const isStatusChange = relevantFields.length === 1 && relevantFields[0] === 'status';

          if (isStatusChange) {
            const statusChange = historyItem.changes.status;
            const fromStatus = statusChange.from === 'in_progress' ? 'In Progress' :
                              statusChange.from === 'on_hold' ? 'On Hold' :
                              statusChange.from === 'resolved' ? 'Resolved' :
                              statusChange.from === 'open' ? 'Open' : statusChange.from;
            const toStatus = statusChange.to === 'in_progress' ? 'In Progress' :
                            statusChange.to === 'on_hold' ? 'On Hold' :
                            statusChange.to === 'resolved' ? 'Resolved' :
                            statusChange.to === 'open' ? 'Open' : statusChange.to;

            displayHistoryItems.push({
              id: 1000 + historyIndex,
              title: "Status changes",
              date: formatDateTime(historyItem.changed_at),
              rawDate: historyItem.changed_at,
              name: `Status changed from <strong class="text-capitalize">${fromStatus}</strong> to <strong class="text-capitalize">${toStatus}</strong> by <strong style="color:#2965AD" class="text-capitalize">${historyItem.changed_by || "-"}</strong>`,
              description: `Reason: ${statusChange && statusChange.reason}`,
            });
          } else {
            // Build description with all field changes
            const changesList = relevantFields
              .map((field: string) => {
                const change = historyItem.changes![field];
                const fieldLabel = formatFieldName(field);
                const fromValue = formatFieldValue(change.from, field);
                const toValue = formatFieldValue(change.to, field);
                return `${fieldLabel} changed from <strong>${fromValue}</strong> to <strong>${toValue}</strong>`;
              })
              .join('<br/>');

            // Check if status is among the changed fields
            const hasStatusChange = relevantFields.includes('status');
            const statusReason = hasStatusChange && historyItem.changes!.status && historyItem.changes!.status.reason
              ? `Reason: ${historyItem.changes!.status.reason}`
              : "";

            displayHistoryItems.push({
              id: 1000 + historyIndex,
              title: "Notice Edited",
              date: formatDateTime(historyItem.changed_at),
              rawDate: historyItem.changed_at,
              name: `${changesList} by <strong style="color:#2965AD" class="text-capitalize">${historyItem.changed_by || "-"}</strong>`,
              description: statusReason,
            });
          }
        }
      });
    }

    // 3. Document Events (Upload/Delete) - Grouped by timestamp and action
    if (historyItems && historyItems.length > 0) {
      const documentEvents = historyItems.filter((item: HistoryItem) =>
        item.entry_type === 'document_event' && item.document_details
      );

      // Group by timestamp and action
      const groupedEvents: Record<string, HistoryItem[]> = {};
      documentEvents.forEach((item: HistoryItem) => {
        const key = `${item.changed_at}_${item.action}`;
        if (!groupedEvents[key]) {
          groupedEvents[key] = [];
        }
        groupedEvents[key].push(item);
      });

      // Create history items for each group
      Object.keys(groupedEvents).forEach((key) => {
        const group = groupedEvents[key];
        if (group.length === 0) return;

        const firstItem = group[0];
        const action = firstItem.action;

        // Build list of files with appropriate prefix
        let displayName = '';
        if (action === 'uploaded') {
          const filesList = group
            .map((item: HistoryItem) => {
              const { filename, file_size } = item.document_details!;
              return `<strong style='word-break: break-all;'>${filename}</strong> (${file_size})`;
            })
            .join('<br/>');
          displayName = `New document(s) have been uploaded:<br/>${filesList} by <strong style="color:#2965AD" class="text-capitalize">${firstItem.changed_by || "-"}</strong>`;
        } else {
          // For deleted files
          const prefix = group.length === 1 ? 'A document has been deleted:' : 'Files have been deleted:';
          const filesList = group
            .map((item: HistoryItem) => {
              const { filename, file_size } = item.document_details!;
              return `<strong style='word-break: break-all;'>${filename}</strong> (${file_size})`;
            })
            .join('<br/>');
          displayName = `${prefix} ${filesList} by <strong style="color:#2965AD" class="text-capitalize">${firstItem.changed_by || "-"}</strong>`;
        }

        displayHistoryItems.push({
          id: key,
          title: action === 'uploaded' ? 'Document(s) Uploaded' : 'Document Deleted',
          date: formatDateTime(firstItem.changed_at),
          rawDate: firstItem.changed_at,
          name: displayName,
          description: '',
        });
      });
    }

    // 5. Notice Created
    const createdBy = (noticeDetail.creator && noticeDetail.creator.name) || "-";

    // Notice Created On
    displayHistoryItems.push({
      id: 3,
      title: "Notice Created On",
      date: formatDateTime(noticeDetail.created_at),
      rawDate: noticeDetail.created_at,
      name: `Created by <strong style="color:#2965AD" class="text-capitalize">${createdBy}</strong>`,
      description: "",
    });
  }

  // Sort by date (newest first)
  displayHistoryItems.sort((a, b) => {
    const dateA = new Date(a.rawDate || 0).getTime();
    const dateB = new Date(b.rawDate || 0).getTime();
    return dateB - dateA;
  });

  const handleDownload = async (fileUrl: string, fileName: string) => {
    if (!fileUrl) {
      toast.error("File URL not available");
      return;
    }
    try {
      await downloadS3FileSecurely(fileName, fileUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Failed to download file");
    }
  };

  return (
    <>
      {isOpen && (
        <div className="licensedetails-overlay" onClick={handleClosePanel}></div>
      )}
      <div className="licensedetails-wrapper notice-wrapper">
        <div className={`licensedetails ${isOpen ? "open" : ""}`} ref={scrollContainerRef}>
          <div className="licensedetails-header pb-0">
            <div className="d-flex justify-content-between align-items-start">
              <h5 className="mb-0 fw-semibold">{noticeDetail && noticeDetail.name}</h5>
              <div className="d-flex justify-content-between align-items-center gap-4">
                {canShowEditButton && (
                  <div
                    className="d-flex align-items-center gap-2 cursor-pointer"
                    onClick={openEditPopup}
                  >
                    <img src={editIcon} alt="editIcon" />
                    <span className="edit_button">Edit</span>
                  </div>
                )}
                <button
                  className="btn-close"
                  aria-label="Close"
                  onClick={handleClosePanel}
                ></button>
              </div>
            </div>

            <div>
              <ul className="progress-section p-0 m-0 mt-4 pl-2">
                <li
                  className={activeTab === "details" ? "active" : ""}
                  onClick={() => setActiveTab("details")}
                >
                  Notice Details
                </li>
                {(noticeDocuments && noticeDocuments.length !== 0) && (
                <li
                  className={activeTab === "documents" ? "active" : ""}
                  onClick={() => setActiveTab("documents")}
                >
                  Documents
                </li>)}
                <li
                  className={activeTab === "history" ? "active" : ""}
                  onClick={() => setActiveTab("history")}
                >
                  History
                </li>
              </ul>
            </div>
          </div>          
          <div className="licensedetails-body" style={{height: "calc(100% + 144px)"}}>


            {/* Notice Details Tab */}
            {activeTab === "details" && (
              <>
                <ul className="licensedetails-text pb-3">
                  {noticeDetails.map((item, index) => (
                    <li key={index}>
                      <div>{item.label}</div>
                      <div style={item.label === "Description" ? { whiteSpace: 'pre-wrap' } : undefined}>{item.value}</div>
                    </li>
                  ))}
                  <li>
                    <div>Notice Status</div>
                    {(hasUserAccess(USER_PRIVILEGES.NOTICE_MANAGER) || hasUserAccess(USER_PRIVILEGES.NOTICE_OWNER) || hasUserAccess(USER_PRIVILEGES.EDIT_NOTICES)) ? (
                      <div className="w-100">
                        <select
                          className="licensedetails-select form-select text-capitalize"
                          value={showStatusPopup ? pendingStatusChange : selectedStatus}
                          onChange={(e) => handleStatusChange(e.target.value)}
                          style={{ width: "180px" }}
                          disabled={selectedStatus === "resolved"}
                        >
                          <option value={selectedStatus}>{getStatusLabel(selectedStatus)}</option>
                          {getAllowedStatusTransitions(selectedStatus).map((status) => (
                            <option key={status} value={status}>
                              {getStatusLabel(status)}
                            </option>
                          ))}
                        </select>
                        {selectedStatus === "resolved" && (
                          <div className="text-muted small mt-1">
                            Resolved notices cannot be changed to any other status.
                          </div>
                        )}
                        {showStatusPopup && (
                          <div className="mt-3 w-100">
                            <label className="mb-2">
                              Reason for status change<span>*</span>
                            </label>
                            <textarea
                              ref={statusNoteRef}
                              className="form-control"
                              placeholder="Add reason for status change"
                              maxLength={1000}
                              rows={4}
                              style={{minHeight: "114px"}}
                              value={statusNote}
                              onChange={(e) => setStatusNote(e.target.value)}
                            ></textarea>
                            <small className="text-muted text-sm d-flex justify-content-end">
                              {statusNote.length}/1000 characters
                            </small>
                            <div className="d-flex gap-2 mt-2">
                              <button
                                type="button"
                                className="button-primary w-100"
                                onClick={handleSaveStatusChange}
                                disabled={isLoading || !statusNote.trim()}
                              >
                                {updatingStatus ? "Updating..." : "Confirm Status Update"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className={`text-capitalize fw-bold color-class ${getStatusColorClass(selectedStatus)}`}>
                        {getStatusLabel(selectedStatus)}
                      </span>
                    )}
                  </li>
                </ul>
              </>
            )}

            {activeTab === "documents" && (
              <>
                <div className="document-container pt-4">
                  {noticeDocuments &&
                  noticeDocuments.length > 0 ? (
                    <div className="document-list">
                      <div className="fw-bold mb-3">Attached Documents</div>
                      {noticeDocuments.map((doc: any) => {

                        var fileName = doc.name || "No file";
                        var fileUrl = doc.file_path || "";

                        return (
                          <div key={doc.id} className="document-item mb-4">
                            <div className="d-flex justify-content-between align-items-center">
                              {/* <label className="form-label w-50 ellipse">
                                {documentName}
                              </label> */}
                              <div className="upload-successful position-relative w-100">
                              <div className="px-4 py-3 d-flex align-items-center justify-content-between">
                                <p className="text-sm fw-semibold m-0 img_name_truncate">
                                  {fileName}
                                </p>
                                <div
                                  style={{
                                    cursor: "pointer",
                                    height: "25px",
                                    width: "25px",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownload(fileUrl, fileName);
                                  }}
                                >
                                  <img
                                    className="d-block"
                                    src={downloadButton}
                                    alt="downloadButton"
                                  />
                                </div>
                              </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-muted h-100 w-100 d-flex justify-content-center align-items-center">
                      No documents uploaded for this notice.
                    </p>
                  )}
                </div>
              </>
            )}

            {/* History Tab */}
             {activeTab === "history" && (
              <>
                <div className="history-accordion">
                  {displayHistoryItems.map((item, index) => (
                    <div key={item.id} className="accordion-item mb-2">
                      <div className="accordion-timeline">
                        <div
                          className={`timeline-dot d-flex justify-content-center align-items-center ${
                            item ? "active" : ""
                          }`}
                        >
                          <div className="progress-checked"></div>{" "}
                          {/* add class "completed" when need to show checked */}
                        </div>
                        {index < displayHistoryItems.length - 1 && (
                          <div className="timeline-line">
                            <div
                              className={`dark-line ${
                                expandedId === item.id ? "expanded" : ""
                              }`}
                            ></div>
                          </div>
                        )}
                      </div>
                      <div className="accordion-content-wrapper">
                        <div
                          className="accordion-header"
                        >
                          <div className="accordion-header-left">
                            <h6
                              className="accordion-title mb-0"
                              dangerouslySetInnerHTML={{ __html: item.title }}
                            ></h6>
                            {item.name && <span className="accordion-name" dangerouslySetInnerHTML={{ __html: item.name }}></span>}
                            {item.description && (
                              <div className="accordion-description" style={{ whiteSpace: 'pre-line', fontSize: '14px', color: '#b3b3b3' }} dangerouslySetInnerHTML={{ __html: item.description }}></div>
                            )}
                            <span className="accordion-date">{item.date}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

        {noticeDetail && (
          <NoticesEditDetails
            isOpen={isEditOpen}
            onClose={closeEditPopup}
            initialData={{
              id: noticeDetail.id || "",
              name: noticeDetail.name || "",
              description: noticeDetail.description || "",
              category_id: (noticeDetail.category && noticeDetail.category.id) || "",
              issued_date: noticeDetail.issued_date || "",
              resolution_date: noticeDetail.resolution_date || "",
              issued_by: noticeDetail.issued_by || "",
              act_id: (noticeDetail.act && noticeDetail.act.id) || "",
              assigned_to: (noticeDetail.owner && noticeDetail.owner.id) || "",
              watcher_ids: (noticeDetail.watchers && noticeDetail.watchers.map((watcher: any) => watcher.id)) || [],
              company_id: (noticeDetail.company && noticeDetail.company.id) || "",
              associate_company_id: (noticeDetail.associate_company && noticeDetail.associate_company.id) || "",
              location_id: (noticeDetail.location && noticeDetail.location.id) || "",
              status: noticeDetail.status || "active",
              documents: noticeDetail.documents || [],
            }}
            onSave={handleSave}
            isLoading={updating}
            onDocumentDelete={() => {
              if (refetchNotice) refetchNotice();
              if (refetch) refetch();
            }}
          />
        )}
        {isLoading && <PageLoader />}
    </>
  );
};
export default NoticeDetail;